// library/model — the Library's (Bibliotek) Model: what the module owns
// beside the frame. The query text and the opened row are FRAME state (the
// active Search tab, mirrored to the URL); the module keeps the last values it
// was shown so it can tell what changed, plus everything that is its own:
// the marked row, the browse order, and the two remote reads (the content
// search for the query's free text, the opened item's own content).
//
// Pure data. Every remote value is a `Remote` (no nullable value + error pair).

import type { NewType } from "@bilda/utils";
import { Remote } from "@bilda/utils";
import type { ExternalError } from "../../app/externalError.js";
import type { LoadedExport } from "../../app/ports/packaging.js";
import type { LoadedFile, LoadedSource } from "../../app/ports/resource.js";
import type { ContentSearch } from "../../components/Search/contentSearch.js";
import type {
    LibrarySort,
    SearchResultKind,
} from "../../components/Search/searchModel.js";

// The row the author has gone into, as the frame shows it.
export type Opened = {
    readonly id: string;
    readonly kind: SearchResultKind;
};

// The kinds whose item view the module loads. A material is edited in place
// through the editor host (its own load); a conversation lives in the chat.
export type LoadedKind = "source" | "package" | "file";

export type ItemContent =
    | NewType<"Item.Source", { readonly source: LoadedSource }>
    | NewType<"Item.Package", { readonly pkg: LoadedExport }>
    | NewType<"Item.File", { readonly file: LoadedFile }>;

// The opened item's own content: nothing to load (the list, a material), or
// one item's read.
export type Detail =
    | NewType<"Detail.Idle">
    | NewType<
          "Detail.Open",
          {
              readonly id: string;
              readonly kind: LoadedKind;
              readonly load: Remote.Remote<ItemContent, ExternalError>;
          }
      >;

export type LibraryModel = {
    // The frame's query text and opened row, as last shown.
    readonly query: string;
    readonly openedId: string | null;
    // The row the arrow keys moved to, or the one last gone into (marked on
    // the way back).
    readonly marked: string | null;
    // The table's sort, remembered per device.
    readonly sort: LibrarySort;
    // The content search (C3) for the query's free words.
    readonly content: ContentSearch;
    readonly detail: Detail;
    // The saved search last applied (its chip keeps the menu while the query
    // moves on, so it can be updated to the new search), and the last saved-
    // search write that failed (shown beside the saved searches until the
    // next one).
    readonly applied: string | null;
    readonly smartFilterFailure: ExternalError | null;
    // The saved search just deleted: its undo is offered until the author's
    // next action (no timer — the affordance is a projection of this).
    readonly deletedSmartFilter: string | null;
};

export const initialLibraryModel = (sort: LibrarySort): LibraryModel => ({
    query: "",
    openedId: null,
    marked: null,
    sort,
    content: { words: [], hits: Remote.notAsked },
    detail: { _tag: "Detail.Idle" },
    applied: null,
    smartFilterFailure: null,
    deletedSmartFilter: null,
});
